/* @flow */
/* eslint-env es6,browser */

/**
 * @description wrapper for FETCH API
 * @module utils/fetch
 * @exports utils/fetch
 * @requires {@link module:utils}
 */

/**
 * @typedef {object} jsonpConfig
 */

import { noop, isObject, isString, extend, param } from '../utils/coreutils';

/**
 * checks status of the response
 *
 * @param {Response} response - Fetch request's response
 * @returns {Promise<*>} - Resolve/Rejected Promise
 */
function status(response: Response): Promise<*> {
  if (response.ok) {
    return Promise.resolve(response);
  }
  return Promise.reject(new Error(response.statusText));
}

/**
 * Extracts json data from fetch response
 *
 * @param {Response} resp - Fetch request's response
 * @returns {Promise<*>} - Resolve/Rejected Promise
 */
function json(resp: Response): Promise<*> {
  // no content case
  if (resp.status === 204) {
    return Promise.resolve({});
  }
  const contentType = resp.headers.get('content-type');
  if (
    contentType &&
    (contentType.includes('application/json') ||
      contentType.includes('text/html')) //RT#493270
  ) {
    return resp.json();
  }
  return Promise.reject(
    new Error(`Invalid content type: ${contentType || 'empty'}`)
  );
}

/**
 * utility func to check fetch support
 *
 * @returns {boolean} - fetch support status
 */
export function isSupported(): boolean {
  return 'fetch' in window;
}

/**
 * utility function for fetch
 *
 * @param {string} url - URL
 * @param {jsonpConfig} o - JSONP Config object
 * @param {Request} req - Request object
 * @returns {Promise<object>} - Returns promise
 */
export function JSON(
  url: string,
  o: jsonpConfig,
  req?: Object
): Promise<Object> {
  if (!isSupported()) {
    return Promise.reject('fetch not supported');
  }

  //default option if nothing provided. Caller can override
  const _defOptions: jsonpConfig = {
    cache: false,
    data: {},
    jsonpCallback: noop,
    timeout: 300,
    logPrefix: '',
    context: window
  };

  //invalid input
  if (!isObject(o) || !isString(url) || !url.length) {
    return Promise.reject('Invalid args!');
  }

  // combined options are stored here
  // intentionally not doing deep merge since we have window object in defOptions
  const opt: jsonpConfig = extend({}, _defOptions, o);
  req = req || {};

  //porting cache from options to Request object
  if (!opt.cache) {
    req['cache'] = 'no-store';
  }

  //serialize data for adding to query param
  if (!url.includes('?')) {
    url += '?';
  } else if (url[url.length - 1] !== '&') {
    url += '&';
  }
  url += param(opt.data);

  return new Promise((resolve, reject) => {
    let loadTimer: ?TimeoutID = setTimeout(
      () => reject(`${opt.logPrefix} fetch failed: timeout`),
      opt.timeout
    );
    /* eslint-disable compat/compat */
    fetch(url, req)
      .then(status)
      .then(json)
      .then(
        data => {
          if (loadTimer) {
            clearTimeout(loadTimer);
            loadTimer = null;
          }
          if (opt.jsonpCallback) {
            opt.jsonpCallback.call(opt.context, data);
          }
          resolve(data);
          return data;
        },
        e => reject(`${opt.logPrefix} ${e.message}`)
      );
    /* eslint-enable compat/compat */
  });
}
